<template>
  <div class="live-square">
    <div class="ls-header">
      <h1 class="ls-title">直播广场</h1>
      <span class="ls-sub">{{ rooms.length > 0 ? `${rooms.length} 个主播正在直播` : '暂无主播开播' }}</span>
      <span v-if="refreshing" class="ls-refreshing">刷新中...</span>
    </div>

    <!-- 正在直播列表 -->
    <div v-if="rooms.length" class="ls-grid">
      <div
        v-for="room in rooms"
        :key="room.id"
        class="ls-card"
        @click="goRoom(room)"
      >
        <div class="ls-cover-wrap">
          <img :src="fullUrl(room.cover, defaultCover)" :alt="room.title" class="ls-cover" @error="handleImgError">
          <span class="ls-live-tag"><i></i>直播中</span>
          <span class="ls-viewers">{{ formatViews(room.view_count) }}人观看</span>
          <span class="ls-play-mask">▶</span>
        </div>
        <div class="ls-info">
          <h3 class="ls-room-title" :title="room.title">{{ room.title || '未命名直播间' }}</h3>
          <div class="ls-anchor">
            <img :src="avatarOf(room)" alt="" class="ls-avatar" @error="handleAvatarError">
            <span class="ls-nickname">{{ room.nickname || ('主播' + room.user_id) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!loading" class="ls-empty">
      <svg viewBox="0 0 24 24" width="64" height="64" fill="none" stroke="#4b5162" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M23 7l-7 5 7 5V7z"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </svg>
      <p>暂无主播正在直播</p>
      <button class="ls-empty-btn" @click="goBroadcast">去开播</button>
    </div>

    <div v-else class="ls-loading">
      <span class="ls-spinner"></span>
      正在加载直播间...
    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { BASE_URL } from '../api/config';
import { getLivingList } from '../api/liveRoom';
import { avatarFallback } from '../utils/avatar';

export default {
  name: 'LiveSquare',
  setup() {
    const router = useRouter();
    const rooms = ref([]);
    const loading = ref(true);
    const refreshing = ref(false);
    const defaultCover = require('@/assets/1.jpg');
    const defaultAvatar = require('@/assets/logo.png');
    let timer = null;

    const fullUrl = (url, fallback) => {
      const source = String(url || '').trim();
      if (!source) return fallback;
      if (/^(https?:|data:|blob:)/i.test(source)) return source;
      return BASE_URL + (source.startsWith('/') ? source : '/' + source);
    };

    const fetchRooms = async (silent = false) => {
      if (!silent) loading.value = true;
      try {
        const res = await getLivingList({ page: 1, pagesize: 60 });
        const list = res.data?.data || res.data?.list || [];
        rooms.value = Array.isArray(list) ? list : [];
      } catch (e) {
        rooms.value = [];
      } finally {
        loading.value = false;
      }
    };

    const refresh = async () => {
      refreshing.value = true;
      try { await fetchRooms(true); } finally { refreshing.value = false; }
    };

    const goRoom = (room) => {
      router.push(`/room/${room.id}`);
    };
    const goBroadcast = () => {
      router.push('/broadcast');
    };
    const formatViews = (views) => {
      const n = Number(views) || 0;
      if (n >= 10000) return (n / 10000).toFixed(1).replace(/\.0$/, '') + '万';
      return n;
    };
    const handleImgError = event => { event.target.src = defaultCover; };
    const handleAvatarError = event => { event.target.src = defaultAvatar; };
    // 头像兜底：空头像时使用「昵称首字+渐变」头像，避免公司 logo 占位
    const avatarOf = (room) => room.avatar ? fullUrl(room.avatar, defaultAvatar) : avatarFallback(room.nickname || '主播', room.user_id);

    onMounted(() => {
      fetchRooms();
      timer = setInterval(refresh, 15000);
    });
    onBeforeUnmount(() => {
      if (timer) clearInterval(timer);
    });

    return {
      rooms,
      loading,
      refreshing,
      defaultCover,
      defaultAvatar,
      fullUrl,
      formatViews,
      goRoom,
      goBroadcast,
      handleImgError,
      handleAvatarError,
      avatarOf
    };
  }
};
</script>

<style scoped>
.live-square {
  min-height: calc(100vh - 56px);
  background: #0c0d11;
  color: #f5f5f5;
  padding: 30px 24px 60px;
}
.ls-header {
  width: min(1180px, 100%);
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.ls-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  background: linear-gradient(135deg, #ff8b28, #ffc05c);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.ls-sub { color: #8a90a0; font-size: 14px; }
.ls-refreshing { color: #ff9b37; font-size: 12px; }

.ls-grid {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}
.ls-card {
  border-radius: 12px;
  overflow: hidden;
  background: #15171d;
  border: 1px solid rgba(255,255,255,.07);
  cursor: pointer;
  transition: transform .2s, box-shadow .2s;
}
.ls-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,.45);
}
.ls-cover-wrap {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #050506;
}
.ls-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform .3s;
}
.ls-card:hover .ls-cover { transform: scale(1.05); }
.ls-live-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 4px;
  background: #ee344c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.ls-live-tag i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
}
.ls-viewers {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba(0,0,0,.6);
  color: #dde0e8;
  font-size: 11px;
}
.ls-play-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 40px;
  background: rgba(0,0,0,0);
  opacity: 0;
  transition: opacity .2s, background .2s;
}
.ls-card:hover .ls-play-mask {
  opacity: 1;
  background: rgba(0,0,0,.25);
}
.ls-info { padding: 12px 14px 14px; }
.ls-room-title {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ls-anchor {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ls-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: #252832;
}
.ls-nickname {
  color: #aeb3bf;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ls-empty {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #7a8191;
}
.ls-empty p { margin: 0; font-size: 16px; }
.ls-empty-btn {
  border: 0;
  border-radius: 20px;
  padding: 9px 26px;
  background: linear-gradient(135deg, #ff7a18, #ff9b37);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.ls-loading {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #8a90a0;
}
.ls-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255,139,40,.25);
  border-top-color: #ff8b28;
  border-radius: 50%;
  animation: ls-spin .8s linear infinite;
}
@keyframes ls-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
  .ls-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .ls-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .live-square { padding: 20px 14px 48px; }
}
</style>
